import { useEffect, useState } from 'react';

/**
 * Tyndo useFreshness — returns a relative-time string for "updated Xs ago".
 *
 * Smart cadence:
 *   - Under a minute → updates every 1s, format "12s"
 *   - Under an hour  → updates every 30s, format "4m"
 *   - Under a day    → updates every 5min, format "2h"
 *   - Beyond         → updates every 1h,   format "3d" / "Mar 4"
 *
 * Returns also a `staleness` enum (`fresh|aging|stale`) calibrated for ops UI:
 *   fresh  ≤ 30s
 *   aging  31s..5min
 *   stale  > 5min
 *
 * Designed to feed `LiveTag`, `StaleDataOverlay`, and table cells that need
 * to show how recent a data point is. Pairs naturally with TanStack Query's
 * `dataUpdatedAt`.
 */
export type Freshness = 'fresh' | 'aging' | 'stale';

export interface UseFreshnessResult {
  label: string;
  staleness: Freshness;
  ageSeconds: number;
}

const FRESH_S = 30;
const AGING_S = 5 * 60;

function formatAge(secs: number): string {
  if (secs < 5) return 'just now';
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h`;
  if (secs < 7 * 86400) return `${Math.round(secs / 86400)}d`;
  // Older than a week — render absolute date.
  const d = new Date(Date.now() - secs * 1000);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function classify(secs: number): Freshness {
  if (secs <= FRESH_S) return 'fresh';
  if (secs <= AGING_S) return 'aging';
  return 'stale';
}

/**
 * Pick the polling interval (ms) for a given age (ms).
 *   < 1min  → 1s,  < 1h → 30s,  < 1d → 5min,  beyond → 1h
 * Returning a discrete cadence band (rather than a continuous value) means the
 * interval effect only re-establishes when the band itself changes, not on
 * every tick.
 */
function cadenceMs(ageMs: number): number {
  if (ageMs < 60000) return 1000;
  if (ageMs < 3600000) return 30000;
  if (ageMs < 86400000) return 5 * 60000;
  return 60 * 60000;
}

export function useFreshness(timestamp?: number | string | Date): UseFreshnessResult {
  const ts = timestamp === undefined ? null : +new Date(timestamp);
  const [now, setNow] = useState(() => Date.now());

  // Drive the interval off the cadence band, not the raw `now`. The band only
  // shifts when the age crosses a threshold (e.g. past 1min), so the interval
  // is established once per band instead of being torn down and rebuilt on
  // every tick. `setNow` uses a functional update so we never read `now`
  // inside the effect (which would otherwise pull it into the deps).
  const interval = ts === null ? 0 : cadenceMs(now - ts);

  useEffect(() => {
    if (ts === null) return;
    const id = window.setInterval(() => setNow(Date.now()), interval);
    return () => window.clearInterval(id);
  }, [ts, interval]);

  if (ts === null) {
    return { label: '—', staleness: 'stale', ageSeconds: Infinity };
  }

  const ageSeconds = Math.max(0, (now - ts) / 1000);
  return {
    label: formatAge(ageSeconds),
    staleness: classify(ageSeconds),
    ageSeconds,
  };
}
